import { v4 as getUuid } from "uuid";
import { onboardingApi } from ".";
import type { BankGiroConfig } from "../types/bankgiro";
import {
	isPackageOfType,
	type OnboardingPackage,
	type OnboardingPackageSet,
	type SelectedPackage,
} from "../types/package";
import type { ProductDefinitionId } from "../types/product";
import { applicationsApiSlice } from "./application";
import {
	configureBankGiro,
	getAvailablePackages,
	getSelectedPackage,
	removeAddon,
	removePackage,
	selectAddon,
	selectPackage,
} from "./endpoints/package";

const packageApiSlice = onboardingApi.injectEndpoints({
	endpoints: (builder) => ({
		selectPackage: builder.mutation<
			null,
			{
				applicationId: string;
				packageId: string;
			}
		>({
			async queryFn({ applicationId, packageId }, queryApi, _extraOptions, fetchWithBaseQuery) {
				try {
					const packageResult = await fetchWithBaseQuery(selectPackage(applicationId, packageId));

					if (packageResult.error) {
						throw new Error(JSON.stringify(packageResult.error));
					}

					await queryApi
						.dispatch(
							applicationsApiSlice.endpoints.fetchSteps.initiate(applicationId, {
								forceRefetch: true,
							}),
						)
						.unwrap();
					await queryApi
						.dispatch(
							packageApiSlice.endpoints.fetchSelectedPackage.initiate(applicationId, {
								forceRefetch: true,
							}),
						)
						.unwrap();

					return { data: null };
				} catch (error) {
					if (error instanceof Error) {
						return {
							error: {
								error: error.message,
								status: "CUSTOM_ERROR",
							},
						};
					}
					if (typeof error === "string") {
						return {
							error: {
								error,
								status: "CUSTOM_ERROR",
							},
						};
					}

					return {
						error: {
							error: "Unknown error",
							status: "CUSTOM_ERROR",
						},
					};
				}
			},
			invalidatesTags: ["AMLQuestions"],
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
		}),
		removePackage: builder.mutation<null, string>({
			async queryFn(applicationId, queryApi, _extraOptions, fetchWithBaseQuery) {
				try {
					const packageResult = await fetchWithBaseQuery(removePackage(applicationId));
					if (packageResult.error) {
						throw new Error(JSON.stringify(packageResult.error.data));
					}

					await queryApi
						.dispatch(
							applicationsApiSlice.endpoints.fetchSteps.initiate(applicationId, {
								forceRefetch: true,
							}),
						)
						.unwrap();
					await queryApi
						.dispatch(
							packageApiSlice.endpoints.fetchSelectedPackage.initiate(applicationId, {
								forceRefetch: true,
							}),
						)
						.unwrap();
					return { data: null };
				} catch (error) {
					return {
						error: {
							error: error as string,
							status: "CUSTOM_ERROR",
						},
					};
				}
			},
			invalidatesTags: ["AMLQuestions"],
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
		}),
		fetchPackages: builder.query<OnboardingPackageSet, string>({
			query: (applicationId) => getAvailablePackages(applicationId),
			transformResponse: (response: { data: OnboardingPackage[] }) => {
				const base = response.data.find(isPackageOfType("BAAS-FÖRETAG-BAS"));
				const grow = response.data.find(isPackageOfType("BAAS-FÖRETAG-VÄXA"));
				const overdraft = response.data.find(isPackageOfType("OVERDRAFTPACKAGE"));
				const foretagarna = response.data.find(isPackageOfType("FÖRETAGARNA-BAS"));
				const foretagarnaCredit = response.data.find(isPackageOfType("FORETAGARNA-CARDONLY"));
				const ehandel = response.data.find(isPackageOfType("E-HANDEL-BAS"));

				if (!base || !grow || !overdraft) throw new Error("expected packages missing");

				const packages: OnboardingPackageSet = [
					base,
					grow,
					overdraft,
					foretagarna,
					foretagarnaCredit,
					ehandel,
				];

				return packages;
			},
			extraOptions: { withAuth: true },
		}),
		fetchSelectedPackage: builder.query<SelectedPackage, string>({
			query: (applicationId) => getSelectedPackage(applicationId),
			transformResponse: (response: { data: SelectedPackage }) => response.data,
			providesTags: ["CurrentApplication"],
			extraOptions: { withAuth: true },
		}),
		removeAndSelectPackage: builder.mutation<
			null,
			{
				applicationId: string;
				packageId: string;
			}
		>({
			async queryFn({ applicationId, packageId }, queryApi, _extraOptions, fetchWithBaseQuery) {
				try {
					const removePackageResult = await fetchWithBaseQuery(removePackage(applicationId));
					const selectPackageResult = await fetchWithBaseQuery(
						selectPackage(applicationId, packageId),
					);

					if (removePackageResult.error || selectPackageResult.error) {
						throw removePackageResult.error
							? new Error(JSON.stringify(removePackageResult.error))
							: new Error(JSON.stringify(selectPackageResult.error));
					}

					await queryApi
						.dispatch(
							applicationsApiSlice.endpoints.fetchSteps.initiate(applicationId, {
								forceRefetch: true,
							}),
						)
						.unwrap();

					await queryApi
						.dispatch(
							packageApiSlice.endpoints.fetchSelectedPackage.initiate(applicationId, {
								forceRefetch: true,
							}),
						)
						.unwrap();
					return { data: null };
				} catch (error) {
					return {
						error: {
							error: error as string,
							status: "CUSTOM_ERROR",
						},
					};
				}
			},
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
			invalidatesTags: ["AMLQuestions"],
		}),
		removeAddon: builder.mutation<
			null,
			{
				applicationId: string;
				// TODO: Fix addons type
				addonIds: string[];
			}
		>({
			async queryFn({ applicationId, addonIds }, queryApi, _extraOptions, fetchWithBaseQuery) {
				try {
					const res = await fetchWithBaseQuery(removeAddon(applicationId, addonIds));

					if (res.error) {
						throw new Error(JSON.stringify(res.error));
					}

					await queryApi
						.dispatch(
							applicationsApiSlice.endpoints.fetchSteps.initiate(applicationId, {
								forceRefetch: true,
							}),
						)
						.unwrap();
					await queryApi
						.dispatch(
							packageApiSlice.endpoints.fetchSelectedPackage.initiate(applicationId, {
								forceRefetch: true,
							}),
						)
						.unwrap();

					return { data: null };
				} catch (error) {
					return {
						error: {
							error: error as string,
							status: "CUSTOM_ERROR",
						},
					};
				}
			},
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
			// invalidatesTags: ['CurrentApplication'],
		}),
		selectAddon: builder.mutation<
			null,
			{
				applicationId: string;
				// TODO: Fix addons type
				addons: {
					productDefinitionId: ProductDefinitionId;
					productId: string;
				}[];
			}
		>({
			async queryFn({ applicationId, addons }, queryApi, _extraOptions, fetchWithBaseQuery) {
				try {
					const res = await fetchWithBaseQuery(selectAddon(applicationId, addons));

					if (res.error) {
						throw new Error(JSON.stringify(res.error));
					}

					await queryApi
						.dispatch(
							applicationsApiSlice.endpoints.fetchSteps.initiate(applicationId, {
								forceRefetch: true,
							}),
						)
						.unwrap();
					await queryApi
						.dispatch(
							packageApiSlice.endpoints.fetchSelectedPackage.initiate(applicationId, {
								forceRefetch: true,
							}),
						)
						.unwrap();
					return { data: null };
				} catch (error) {
					return {
						error: {
							error: error as string,
							status: "CUSTOM_ERROR",
						},
					};
				}
			},
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
			// invalidatesTags: ['CurrentApplication'],
		}),
		configureBankGiro: builder.mutation<void, { applicationId: string; config: BankGiroConfig }>({
			query: ({ applicationId, config }) => configureBankGiro(applicationId, config),
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
			invalidatesTags: ["CurrentApplication"],
		}),
		selectAndConfigureBankGiro: builder.mutation<
			null,
			{
				applicationId: string;
				config: BankGiroConfig;
				existingId?: string;
				hasBankgiroProduct?: boolean;
			}
		>({
			async queryFn(
				{ applicationId, config, existingId, hasBankgiroProduct },
				queryApi,
				_extraOptions,
				fetchWithBaseQuery,
			) {
				try {
					if (existingId) {
						const res = await fetchWithBaseQuery(removeAddon(applicationId, [existingId]));

						if (res.error) {
							throw new Error(JSON.stringify(res.error));
						}
					}

					// temporary until all packages contain bankgiro product:
					if (!hasBankgiroProduct) {
						const selected = await fetchWithBaseQuery(
							selectAddon(applicationId, [
								{
									productDefinitionId: "BANKGIRO",
									productId: getUuid(),
								},
							]),
						);

						if (selected.error) {
							throw new Error(JSON.stringify(selected.error));
						}
					}

					const res = await fetchWithBaseQuery(configureBankGiro(applicationId, config));

					if (res.error) {
						throw new Error(JSON.stringify(res.error));
					}

					await queryApi
						.dispatch(
							applicationsApiSlice.endpoints.fetchSteps.initiate(applicationId, {
								forceRefetch: true,
							}),
						)
						.unwrap();

					return { data: null };
				} catch (error) {
					return {
						error: {
							error: error as string,
							status: "CUSTOM_ERROR",
						},
					};
				}
			},
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
		}),
	}),
});

export const {
	useFetchPackagesQuery,
	useFetchSelectedPackageQuery,
	useSelectPackageMutation,
	useRemoveAndSelectPackageMutation,
	useRemovePackageMutation,
	useRemoveAddonMutation,
	useSelectAddonMutation,
	useSelectAndConfigureBankGiroMutation,
} = packageApiSlice;
